import { useId, useState, type FormEvent, type ReactNode } from 'react';
import { useTranslation } from '@/lib/i18n';
import { Button } from './button';
import { Dialog } from './dialog';
import { Field } from './field';
import { Textarea } from './textarea';

export function ReasonDialog({
  title,
  description,
  label,
  placeholder,
  minLength = 3,
  maxLength = 1000,
  submitLabel,
  destructive,
  loading,
  onSubmit,
  onClose,
  children,
}: {
  title: string;
  description?: string;
  label?: string;
  placeholder?: string;
  minLength?: number;
  maxLength?: number;
  submitLabel?: string;
  destructive?: boolean;
  loading?: boolean;
  onSubmit: (reason: string) => unknown;
  onClose: () => void;
  children?: ReactNode;
}) {
  const common = useTranslation('common');
  const formId = useId();
  const [reason, setReason] = useState('');
  const [tried, setTried] = useState(false);
  const [pending, setPending] = useState(false);
  const busy = Boolean(loading) || pending;
  const trimmed = reason.trim();
  const tooShort = trimmed.length < minLength;
  const error =
    tried && tooShort
      ? trimmed.length === 0
        ? common.t('form.field_required')
        : common.t('reason.too_short', { min: minLength })
      : undefined;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setTried(true);
    if (tooShort || busy) return;
    const result = onSubmit(trimmed);
    if (!(result instanceof Promise)) return;
    setPending(true);
    try {
      await result;
    } finally {
      setPending(false);
    }
  };

  const close = () => {
    if (!busy) onClose();
  };

  return (
    <Dialog
      open
      onClose={close}
      title={title}
      description={description}
      footer={
        <>
          <Button type="button" variant="outline" onClick={close} disabled={busy}>
            {common.t('action.cancel')}
          </Button>
          <Button
            type="submit"
            form={formId}
            variant={destructive ? 'destructive' : 'default'}
            loading={busy}
          >
            {submitLabel ?? common.t('action.confirm')}
          </Button>
        </>
      }
    >
      <form id={formId} onSubmit={(event) => void submit(event)} noValidate className={children ? 'space-y-4' : undefined}>
        {children}
        <Field
          label={label ?? common.t('field.reason')}
          required
          error={error}
          hint={common.t('reason.min_hint', { min: minLength })}
          hintBelow
        >
          <Textarea
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder={placeholder}
            maxLength={maxLength}
            rows={4}
            disabled={busy}
          />
        </Field>
      </form>
    </Dialog>
  );
}
